import {
	CodeValue,
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { StatusBadgeDisplay } from '@/components/ui/status-badge-display';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import type { TerminalViewModel } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { FileText, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { GATE_QUERY } from '../gates.gql.tsx';

interface GateDetailsProps {
	gateId: string;
}

export const GateDetails = ({ gateId }: GateDetailsProps) => {
	const { t } = useTranslation('common');
	const { t: tG } = useTranslation('gates');
	const { data, isLoading, isError } = useOne<TerminalViewModel>({
		resource: 'gates',
		id: gateId,
		meta: {
			gqlQuery: GATE_QUERY,
			dataPath: 'terminal'
		}
	});

	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 300,
		showInitialLoading: false
	});

	const gate = data?.data;

	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-32 h-4 rounded animate-pulse bg-muted" />
				<div className="w-20 h-4 rounded animate-pulse bg-muted" />
				<div className="space-y-2">
					<div className="w-full h-3 rounded animate-pulse bg-muted" />
					<div className="w-3/4 h-3 rounded animate-pulse bg-muted" />
				</div>
			</div>
		);
	}

	if (!isLoading && (isError || !gate)) {
		return (
			<div className="py-8 text-sm text-center text-muted-foreground">
				{tG('details.loadError')}
			</div>
		);
	}

	if (!gate) {
		return null;
	}

	return (
		<Tabs defaultValue="details" className="w-full">
			<TabsList className="gap-2 bg-transparent p-0">
				<TabsTrigger value="details" className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold">
					<FileText className="w-4 h-4" />
					<span>{t('tabs.details')}</span>
				</TabsTrigger>
				<TabsTrigger value="audit" align="right" className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold">
					<History className="w-4 h-4" />
					<span>{t('tabs.audit')}</span>
				</TabsTrigger>
			</TabsList>

			<TabsContent value="details" className="space-y-6 text-base mt-6">
				<DetailSection title={t('sections.properties')}>
					<DetailFieldsGrid>
						<DetailField
							label={tG('details.terminalType')}
							value={
								gate.terminalType?.name ??
								tG('details.unassigned')
							}
						/>
						<DetailField
							label={tG('details.status')}
							value={
								<StatusBadgeDisplay
									isActive={Boolean(gate.isActive)}
									activeLabel={tG('details.active')}
									inactiveLabel={tG('details.inactive')}
								/>
							}
						/>
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={t('sections.configuration')}>
					<DetailFieldsGrid>
						<DetailField
							label={tG('details.ipAddress')}
							value={<CodeValue>{gate.ipAddress}</CodeValue>}
						/>
						<DetailField
							label={tG('details.gateKey')}
							value={<CodeValue>{gate.gateKey}</CodeValue>}
						/>
					</DetailFieldsGrid>
				</DetailSection>
			</TabsContent>

			<TabsContent value="audit" className="mt-6">
				<AuditTab data={{}} />
			</TabsContent>
		</Tabs>
	);
};
